import { defineMessages } from 'react-intl'
import { shallowEqual } from 'react-redux'

import { AppError } from '@app/errors/AppError'

import { createSelector } from '@app/utils/createSelector'
import { noopReturn } from '@app/utils/noop'
import { PriceInputValue } from '@app/utils/PriceInputValue'
import { printCurrency } from '@app/utils/printCurrency'

import type { ApiAcademyCart, ApiAcademyPromocode } from '@app/packages/api/v2/types/entities'
import type { AcademyInvoiceCreateRequest } from '@app/packages/api/v2/types/params'
import { asError } from '@app/packages/asError/asError'
import type { Result } from '@app/packages/Result/Result'
import { resolveThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { getProgress } from '@app/store/actions/contexts/progress'
import { createReduxSlice } from '@app/store/redux_slice'
import { cardResultSelector } from '@app/store/slices/card'
import { stripeEnabledSelector } from '@app/store/slices/features'
import { intlSelector } from '@app/store/slices/intl'
import { profileUserSelector } from '@app/store/slices/profile'
import { shortSessionUuidSelector } from '@app/store/slices/short_session'
import { createThunk } from '@app/store/thunk'

export class PromocodeNotApplicableError extends AppError {}

// Slices are declared before the selectors/actions that reference them to avoid a TDZ error
// (createSelector reads slice.selector at module evaluation time).
const cartInvoiceSlice = createReduxSlice<Result<ApiAcademyCart>>('academy_cart_invoice')
const promocodeSlice = createReduxSlice<Result<ApiAcademyPromocode>>('academy_promocode')
const cartInvoiceMetaSlice = createReduxSlice<AcademyCartInvoiceMeta>('academy_cart_invoice_meta')

export const setAcademyInvoice = (invoice: Result<ApiAcademyCart> | null) => (invoice ? cartInvoiceSlice.set(invoice) : cartInvoiceSlice.remove())

export const setAcademyPromocode = (promocode: Result<ApiAcademyPromocode> | null) => (promocode ? promocodeSlice.set(promocode) : promocodeSlice.remove())

export const setAcademyInvoiceMeta = (meta: Partial<AcademyCartInvoiceMeta>) =>
  cartInvoiceMetaSlice.update(current => {
    const base = current ?? DEFAULT_CART_INVOICE_META
    const next = { ...base, ...meta }
    return shallowEqual(next, base) ? base : next
  })

export const cartInvoiceSelector = createSelector([cartInvoiceSlice.selector], invoice => invoice ?? null)

export const promocodeSelector = createSelector([promocodeSlice.selector], promocode => promocode ?? null)

export const cartInvoiceMetaSelector = createSelector([cartInvoiceMetaSlice.selector], meta => meta ?? DEFAULT_CART_INVOICE_META)

export const cartPromocodeStateSelector = createSelector(
  [cartInvoiceSelector, promocodeSelector, cartInvoiceMetaSelector, intlSelector],
  (invoice, promocode, meta, { formatMessage }): null | { type: 'loading' } | { type: 'valid'; description: string } | { type: 'error'; error: Error } => {
    if (meta.loading) return { type: 'loading' }
    if (!promocode) return null
    if (promocode.error) return { type: 'error', error: promocode.value as Error }
    if (invoice && !invoice.error) {
      if (parseFloat(invoice.value.attributes.discount) <= 0) {
        return {
          type: 'error',
          error: new PromocodeNotApplicableError('Promocode is not applicable to items in cart')
            .withLocalizedMessage(formatMessage(messages.promocode_not_applicable))
            .withStatus(400),
        }
      }
    }
    return { type: 'valid', description: promocode.value.attributes.description }
  }
)

export function purchaseLessons(
  data: {
    name: string
    kidName: string
    kidAge: number
    email: string
    phone: string
    promocode?: string
    lesson_ids: string[]
  },
  { withProgress = false }: { withProgress?: boolean } = {}
) {
  return createThunk<
    Promise<
      | { type: 'success'; data: AcademyInvoiceCreateRequest; paymentMethod: 'card_api' | 'academy_api' | 'card_modal' | 'card_modal_guest' }
      | { type: 'cancel' }
      | { type: 'error'; stage: 'invoice' | 'purchase'; data: Error }
    >
  >(async (dispatch, getState) => {
    const [{ postAcademyInvoices, postAcademyInvoicesByIdPayment }, { postChargesCard }, { callV2Api }, { getIntl }, { purchaseInvoiceViaChargeModal }] =
      await Promise.all([
        import('@app/packages/api/v2/actions/academy'),
        import('@app/packages/api/v2/actions/charges'),
        import('@app/store/actions/api'),
        import('@app/store/actions/initial'),
        import('@app/store/actions/payment'),
      ])
    const progress = dispatch(getProgress())
    const session_id = shortSessionUuidSelector(getState())
    const { formatMessage } = dispatch(getIntl())

    const invoiceData: AcademyInvoiceCreateRequest = {
      email: data.email,
      phone: data.phone,
      name: data.name,
      session_id,
      items: data.lesson_ids.map(id => ({ lesson_id: id, age: data.kidAge, name: data.kidName })),
      promocode: data.promocode,
    }

    const wait = withProgress ? progress.wrap.bind(progress) : noopReturn

    const invoiceResult = await wait(dispatch(callV2Api(postAcademyInvoices(invoiceData)))).then(
      resp => ({ ok: true as const, resp }),
      (e: unknown) => ({ ok: false as const, error: asError(e) })
    )

    if (!invoiceResult.ok) return { type: 'error', stage: 'invoice', data: invoiceResult.error }

    const invoice = invoiceResult.resp.data

    const paymentMethod = await wait(
      (async () => {
        if (parseFloat(invoice.attributes.total) <= 0) return 'academy_api'
        if (profileUserSelector(getState())?.account_type !== 'parent') return 'card_modal_guest'
        const card = await dispatch(resolveThunkSelector(cardResultSelector))
        if (!card) return 'card_modal'
        return 'card_api'
      })()
    )

    const stripeEnabled = await dispatch(resolveThunkSelector(stripeEnabledSelector))

    const error =
      paymentMethod === 'card_api'
        ? await wait(dispatch(callV2Api(postChargesCard(invoice.attributes.tracking_id))))
            .then((): { type: 'error'; error: Error } | null => null)
            .catch((e: unknown) => ({ type: 'error', error: asError(e) }) as const)
        : paymentMethod === 'academy_api'
          ? await wait(dispatch(callV2Api(postAcademyInvoicesByIdPayment(invoice.id))))
              .then((): { type: 'error'; error: Error } | null => null)
              .catch((e: unknown) => ({ type: 'error', error: asError(e) }) as const)
          : paymentMethod === 'card_modal'
            ? await dispatch(
                purchaseInvoiceViaChargeModal({
                  invoiceId: invoice.attributes.tracking_id,
                  amount: PriceInputValue.formatted(invoice.attributes.total, invoice.attributes.currency),
                  description: formatMessage(messages.purchase_lessongs_description, {
                    count: data.lesson_ids.length,
                    sum: printCurrency(invoice.attributes.total, invoice.attributes.currency),
                  }),
                  providers: stripeEnabled ? undefined : ['cloudpayments'],
                })
              ).then((result): { type: 'cancel' } | null => (result.type === 'cancel' ? { type: 'cancel' } : null))
            : await dispatch(
                purchaseInvoiceViaChargeModal({
                  invoiceId: invoice.attributes.tracking_id,
                  amount: PriceInputValue.formatted(invoice.attributes.total, invoice.attributes.currency),
                  description: formatMessage(messages.purchase_lessongs_description, {
                    count: data.lesson_ids.length,
                    sum: printCurrency(invoice.attributes.total, invoice.attributes.currency),
                  }),
                  providers: stripeEnabled ? undefined : ['cloudpayments'],
                  canBind: false,
                })
              ).then((result): { type: 'cancel' } | null => (result.type === 'cancel' ? { type: 'cancel' } : null))

    if (error) {
      if (error.type === 'cancel') return { type: 'cancel' }
      return { type: 'error', stage: 'purchase', data: error.error }
    }

    return { type: 'success', data: invoiceData, paymentMethod }
  })
}

type AcademyCartInvoiceMeta = {
  loading: boolean
  /** active when user fills form on mobile */
  prePurchasing: boolean
  purchasing: boolean
  purchaseError: Error | null
}

const DEFAULT_CART_INVOICE_META: AcademyCartInvoiceMeta = { loading: false, prePurchasing: false, purchasing: false, purchaseError: null }

const messages = defineMessages({
  promocode_not_applicable: { id: "store.slices.academy.invoice.messages.promocode_not_applicable.2271", defaultMessage: 'Промокод не может быть применен' },
  purchase_lessongs_description: { id: "store.slices.academy.invoice.messages.purchase_lessongs_description.8c53", defaultMessage: '{count}\u00a0{count, plural, one {занятие} few {занятия} other {занятий}} на сумму {sum}' },
})
